import React, { useCallback, useEffect, useRef, useState } from 'react'
import Link from '../../components/link/Link'
import KeyboardArrowLeftIcon from '@material-ui/icons/KeyboardArrowLeft'
import { FormattedMessage, useIntl } from 'react-intl'
import { formatDateLong } from '../../utils/formatDate'
import parse from 'html-react-parser'
import { useHistory, useRouteMatch } from 'react-router'
import { connect, useDispatch, useSelector } from 'react-redux'
import { useMediaQuery } from 'react-responsive'
import { ClassValue } from 'classnames/types'
import classNames from 'classnames'
import { RootState } from '../reducers'
import { displaySnackbar } from '../reducers/snackBar/actions'
import fetchFactory from '../../utils/fetchFactory'
import { AlertModel } from '../../types/alert/alertModel'
import links from '../../enums/linksEnum'
import { saveAlerts } from '../reducers/alert/actions'
import errorCatcher from '../../utils/errorCatcher'
import Loader from '../../components/loader/Loader'
import { useStyle } from './style'
import { AccessibilityModel } from '../../types/accessibility/accessibilityModel'

const COMPONENT_NAME = 'details_alert'
const initialAlert: AlertModel = {
	id: '',
	beginning: '',
	title: '',
	information: '',
	description: ''
}

interface MatchParamsModel {
	id: string
}

interface AcceptingProps {
	content?: AlertModel
}

interface Store {
	accessibility: AccessibilityModel
}

type DetailsProps = AcceptingProps & Store

const Details: React.FC<DetailsProps> = (
	{
		content,
		accessibility
	}
) => {
	const classes = useStyle({ accessibility })
	const history = useHistory()
	const match = useRouteMatch<MatchParamsModel>()
	const intl = useIntl()
	const alerts = useSelector((state: RootState) => state.alert)
	const [selectedAlert, setSelectedAlert] = useState<AlertModel>(content || initialAlert)
	const dispatch = useDispatch()
	const oldLanguage = useRef(intl.locale)
	const [loading, setLoading] = useState(false)
	const errorMessage = intl.formatMessage(
		{
			id: 'topAlert.details.error',
			defaultMessage: 'L\'alerte demandée n\'a pas été trouvée',
			description: 'Error message'
		}
	)

	const isLargeScreen = useMediaQuery(
		{
			query: '(min-width: 1025px)'
		}
	)

	const detailContainerClass: ClassValue = classNames(
		classes.detailsContainerStyle,
		isLargeScreen ? classes.detailsContainerLargeScreenStyle : classes.detailsContainerSmallScreenStyle
	)

	const textContainerClass: ClassValue = classNames(
		classes.textContainerStyle,
		{
			[classes.textContainerLargeScreenStyle]: isLargeScreen,
			[classes.textContainerSmallScreenStyle]: !isLargeScreen
		}
	)

	const titleClass: ClassValue = classNames(
		classes.title1,
		classes.colorAccessibility,
		classes.titleStyle
	)

	const dateClass: ClassValue = classNames(
		classes.text15,
		classes.colorBlue,
		classes.dateStyle,
		isLargeScreen ? classes.dateLargeScreenStyle : classes.dateSmallScreenStyle
	)

	const goToDashboard = useCallback(
		() => {
			history.push(links.dashboard)
		}, [history]
	)

	const checkId = useCallback(
		(alertsParam: AlertModel[], idInUrl: string) => {
			const alert = alertsParam.find(
				(alert) => alert.id === idInUrl
			)

			if (!!alert) {
				setSelectedAlert(alert)
			} else {
				dispatch(
					displaySnackbar(
						{
							id: COMPONENT_NAME,
							message: errorMessage,
							open: true,
							hideIcon: true
						}
					)
				)

				goToDashboard()
			}
		}, [dispatch, errorMessage, goToDashboard]
	)

	useEffect(
		() => {
			setLoading(true)
			const idInUrl = match.params.id
			window.scrollTo(0, 0)

			if ((alerts.length === 0 || oldLanguage.current !== intl.locale) && !content) {
				fetchFactory(`/api/ec_${process.env.REACT_APP_TARGET}/information/getTopAlert?language=${intl.locale}`)
					.then(
						(alertsResponse: any) => {
							dispatch(
								saveAlerts(alertsResponse)
							)

							oldLanguage.current = intl.locale
							checkId(alertsResponse, idInUrl)
						}
					)
					.catch(
						(err) => {
							dispatch(
								errorCatcher(err, COMPONENT_NAME)
							)
						}
					)
					.finally(
						() => {
							setLoading(false)
						}
					)
			} else {
				checkId(alerts, idInUrl)
				setLoading(false)
			}
		}, [alerts, checkId, dispatch, intl.locale, match.params.id, content]
	)

	const back = () => {
		history.goBack()
	}

	if (loading) {
		return (
			<Loader />
		)
	}

	return (
		<div className={detailContainerClass}>
			<div className={textContainerClass}>
				<div className="link">
					{
						!content && (
							<Link
								color="colorAccessibility"
								textStyle="text4"
								hasChevron={false}
								onClick={back}
							>
								<KeyboardArrowLeftIcon />

								<FormattedMessage
									id="app.back"
									defaultMessage="Retour"
									description="Back button"
								/>
							</Link>
						)
					}

					<h1 className={titleClass}>{selectedAlert.title}</h1>

					<div className={dateClass}>
						<span>
							{formatDateLong(new Date(selectedAlert.beginning), intl.locale)}
						</span>
					</div>

					<span>
						{parse(selectedAlert.description || '')}
					</span>
				</div>
			</div>
		</div>
	)
}

const mapStateToProps = (state: RootState) => (
	{
		accessibility: state.accessibility
	}
)

export default connect(mapStateToProps)(Details)
